import type ClientTypeOptInState from "owa-service/lib/contract/ClientTypeOptInState";
import type { ReadOnlyPolicySettingsType, ReadOnlyUserOptionsType, } from "owa-service/lib/ReadOnlyTypes";
import { getAccountScopeUserSettings } from "owa-session-store";
import type { MailboxInfo } from "owa-client-types";
import { getApplicationSettings } from "owa-application-settings";
import { isAccountExplicitLogon } from "owa-account-shared-mailbox-utils";

export enum ModuleForTasks {
    /** User cannot access any tasks functionality */
    None,
    /** User can access legacy Tasks module */
    LegacyTasks,
    /** User can access modern To Do module */
    ToDo
}

/**
 * Check which module (if any) that the user can access for tasks functionality,
 * based on user type and To Do cutover status
 */
export function getModuleForTasks(mailboxInfo: MailboxInfo): ModuleForTasks {
    const isUserCutover = isToDoModuleEnabled(mailboxInfo);
    if (isUserTypeSupportedForToDo(mailboxInfo) && isUserCutover) {
        return ModuleForTasks.ToDo;
    }
    
    return ModuleForTasks.None;
}

/**
 * Check if user can access modern To Do module
 *
 * NOTE: A return value of `false` should NOT be treated as meaning user can access legacy Tasks module
 *       This is only safe to do IF `getModuleForTasks` is previously called and does not return `ModuleForTasks.None`
 */
export function canShowToDoModule(mailboxInfo: MailboxInfo): boolean {
    return isUserTypeSupportedForToDo(mailboxInfo) && isToDoModuleEnabled(mailboxInfo);
}

/**
 * Check if user can access modern To Do integration features
 *
 * Note that this is identical to `canShowToDoModule` except
 * - user opt-in setting is ignored and
 * - new ToDo.isToDoFeaturesEnabled application setting is applied here
 */
export function canUseToDoFeatures(mailboxInfo: MailboxInfo): boolean {
    return (getApplicationSettings("ToDo", mailboxInfo).isToDoFeaturesEnabled &&
        isUserTypeSupportedForToDo(mailboxInfo) &&
        isToDoModuleEnabled(mailboxInfo, true));
}

/**
 * Check if user type is supported by modern To Do module
 */
function isUserTypeSupportedForToDo(mailboxInfo: MailboxInfo): boolean {
    const isExplicitLogon = isAccountExplicitLogon(mailboxInfo);
    const isShadowMailbox = 
    /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
     * Adding IsShadowMailbox to restricted properties/methods.
     *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
    getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.IsShadowMailbox;
    return !isExplicitLogon && !isShadowMailbox;
}

/**
 * Check if modern To Do module is enabled for the user
 */
function isToDoModuleEnabled(mailboxInfo: MailboxInfo, ignoreUserOptin?: boolean): boolean {
    const userConfiguration = getAccountScopeUserSettings(mailboxInfo);
    const userOptions = userConfiguration.UserOptions;
    const policySettings = userConfiguration.PolicySettings;
    
    return !!(userOptions?.ReactOptinSettings &&
        // To Do cutover takes precedence
        (userOptions.ReactOptinSettings.TasksGraduatedFromBeta ||
            // otherwise, check that To Do module is enabled AND both tenant and user have opted in
            (userOptions.ReactOptinSettings.TasksEnabled &&
                (ignoreUserOptin || isUserOptedIntoTodo(userOptions)) &&
                isTodoAllowedByOutlookBetaPolicySetting(policySettings))) &&
        userOptions.ReactOptinSettings.TasksRedirectUrl !== null);
}

/**
 * Determine if user has opted into modern To Do module
 *
 * Note that `TasksGraduatedFromBeta` (To Do cutover) will take precedence over user opt-in setting
 */
function isUserOptedIntoTodo(userOptions: ReadOnlyUserOptionsType): boolean {
    if (userOptions?.ReactOptinSettings?.TasksShouldUseSharedToggle) {
        // If we are using shared toggle, check ClientTypeOptInState.
        // With React Mail, Cal, People starting to cutover, but not Tasks,
        // we cannot assume that the user wants React Tasks as well.
        return userOptions.ClientTypeOptInState !== 1;
    }
    
    // Not using a shared toggle (e.g. Consumer), users are auto-opted in.
    return true;
}

/**
 * Determine if modern To Do module is allowed by tenant policy
 *
 * The scenario here is that Mail/Calendar has cutover, so we are in React.
 *
 * But the admin has turned off `OutlookBetaToggleEnabled` OWA Mailbox Policy to prevent Tasks from
 * redirecting until To Do is Tier-D compliant.
 *
 * Note that `TasksGraduatedFromBeta` (To Do cutover) will take precedence over tenant policy
 */
function isTodoAllowedByOutlookBetaPolicySetting(policySettings?: ReadOnlyPolicySettingsType): boolean {
    if (policySettings && policySettings.OutlookBetaToggleEnabled === false) {
        return false;
    }
    
    return true;
}
